import React, { useState, useEffect } from "react";
import { X, Send, Loader2, Calendar } from "lucide-react";
import toast from "react-hot-toast";
import { counterOfferToBuyer } from "../../../api/propertyManagement";

/**
 * Agent-initiated counter offer back to the buyer.
 * The agent edits the counter price and key terms (deposit, settlement) and
 * sends it — the buyer receives a branded counter-offer email.
 */
const CounterToBuyerModal = ({ isOpen, onClose, offer, onSent }) => {
  const [counterAmount, setCounterAmount] = useState("");
  const [depositAmount, setDepositAmount] = useState("");
  const [settlementDays, setSettlementDays] = useState("");
  const [customSettlementDate, setCustomSettlementDate] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Prefill from the offer each time the modal opens.
  useEffect(() => {
    if (!isOpen || !offer) return;
    const round = (v) =>
      v === null || v === undefined || v === "" ? "" : Math.round(parseFloat(v));
    setCounterAmount(
      round(offer.counter_amount || offer.offer_amount || offer.offer_amount_min)
    );
    setDepositAmount(round(offer.deposit_amount));
    setSettlementDays(offer.settlement_days ?? "");
    setCustomSettlementDate(
      offer.custom_settlement_date
        ? String(offer.custom_settlement_date).slice(0, 10)
        : ""
    );
    setNote("");
  }, [isOpen, offer]);

  if (!isOpen || !offer) return null;

  const buyerName =
    `${offer.first_name || offer.buyer_name || ""} ${offer.last_name || ""}`.trim();

  const handleSubmit = async () => {
    const amount = parseFloat(counterAmount);
    if (!amount || amount <= 0) {
      toast.error("Enter a valid counter amount");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        counter_amount: amount,
        note,
      };
      if (depositAmount !== "") payload.deposit_amount = parseFloat(depositAmount);
      if (settlementDays !== "") payload.settlement_days = parseInt(settlementDays, 10);
      // Always include so clearing the date is honoured by the backend.
      payload.custom_settlement_date = customSettlementDate || "";

      const res = await counterOfferToBuyer(offer.id, payload);
      const data = res?.data || {};
      toast.success("Counter offer sent to buyer");
      if (data.email_sent === false) {
        toast.error("Buyer email failed to send (will retry)", { duration: 4500 });
      }
      onSent?.(data);
      onClose();
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Failed to send counter offer to buyer";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="px-6 py-5 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-orange-100">
              <Send className="w-5 h-5 text-orange-700" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Send Counter Offer to Buyer</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {buyerName || "Buyer"} · {offer.buyer_email || offer.email}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
              Counter Price <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">$</div>
              <input
                type="number"
                value={counterAmount}
                onChange={(e) => setCounterAmount(e.target.value)}
                placeholder="e.g. 380000"
                className="w-full pl-8 pr-4 py-3 bg-orange-50/40 border-2 border-orange-200 rounded-xl text-lg font-black text-orange-700 outline-none focus:border-orange-400 transition-all"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                Deposit
              </label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">$</div>
                <input
                  type="number"
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(e.target.value)}
                  placeholder="Deposit"
                  className="w-full pl-8 pr-3 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 outline-none focus:border-orange-400 transition-all"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                Settlement (Days)
              </label>
              <input
                type="number"
                value={settlementDays}
                onChange={(e) => setSettlementDays(e.target.value)}
                placeholder="e.g. 60"
                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 outline-none focus:border-orange-400 transition-all"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
              Custom Settlement Date
            </label>
            <div className="relative">
              <Calendar size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="date"
                value={customSettlementDate}
                onChange={(e) => setCustomSettlementDate(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 outline-none focus:border-orange-400 transition-all"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
              Note to buyer (optional)
            </label>
            <textarea
              rows={4}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Explain the counter terms or add a personal note..."
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-orange-200/50 focus:border-orange-400 transition-all resize-none placeholder:text-gray-400"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              disabled={submitting}
              className="flex-1 py-3 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-all disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 py-3 rounded-xl bg-orange-600 text-white text-sm font-semibold hover:bg-orange-700 transition-all disabled:opacity-50 shadow-lg shadow-orange-600/25 flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" /> Send Counter Offer
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CounterToBuyerModal;
